// ═══════════════════════════════════════════════════════════════════════════
// repartoEnvio — la REGLA de cómo se reparte el envío del seller entre las
// líneas de una orden. Fuente única de la regla (ADR 078).
// ───────────────────────────────────────────────────────────────────────────
// MIRROR: functions/lib/repartoEnvio.js (CJS). Si tocás la regla acá, tocala
// allá — y no hace falta acordarse: `functions/lib/repartoEnvio.test.js`
// ejecuta LOS DOS sobre los mismos insumos y falla si dan números distintos.
// Es la diferencia con los mirrors viejos (`rentabilidad.js`), que confían en
// un comentario.
//
// LA REGLA (decisión de negocio, reporte ymCwUxBd5yxnwlXu3oa6, "opción A").
// Mateo Lozano, 8-jul-2026: *"ML solo cobra el envío de los productos por
// encima de $33.000 en argentina o con free_shipping como criterio general"*.
// Decisión del 13-jul: *"vamos con la opción A"*, con su ejemplo numérico
// (carrito de $15.000 de envío repartido entre los DOS ítems con free
// shipping; el de ticket bajo en 0).
//
// ML da UN solo costo de envío por orden, no por línea. Repartirlo entre TODAS
// las líneas por subtotal le carga envío al ítem que no lo incurre y le
// hunde el margen. La regla es: se reparte SOLO entre los que califican, por
// subtotal entre ESOS.
//
// FALLBACK DECLARADO: sin mapa, o con mapa donde NINGÚN ítem de la orden
// califica, se vuelve al reparto entre todas por subtotal. No se pierde un
// costo real cuando no se puede identificar a quién le corresponde (p. ej.
// publicaciones que todavía no se sincronizaron).
//
// INVARIANTE que hace barato el lado backend: en una orden de UNA sola línea
// los dos criterios dan el MISMO número (si califica, el prorrateo es 1; si
// no, cae al fallback y también es 1). Por eso el backend sólo necesita la
// señal de los ítems que aparecen en órdenes de 2+ líneas. Tiene test.
// ═══════════════════════════════════════════════════════════════════════════

import { ORIGENES_COSTO } from '../rentabilidad'

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * Id de item de canal de una publicación, tolerante a los TRES shapes que
 * circulan por el repo:
 *   - legacy (`pubCanonicaALegacy`)      → `itemId` / `id` = MLA…
 *   - canónico crudo (`useDashboardData`) → `externalId` = MLA…, `id` = docId
 *     AUTOGENERADO de Firestore (`publicacionesRepo.js:225`)
 *   - doc canónico del Admin SDK          → `externalId`
 *
 * `externalId` va ANTES que `id` a propósito: leer `id` de un doc canónico
 * devuelve el id autogenerado y el mapa queda con claves que ningún
 * `order_item` puede matchear — el bug silencioso que este módulo cierra.
 */
export function itemIdDePublicacion(p) {
  const id = p?.itemId ?? p?.externalId ?? p?.id
  return id == null ? null : String(id)
}

/** Id de item de canal de una línea de orden (shape ML crudo). */
export function itemIdDeOrderItem(oi) {
  const id = oi?.item?.id
  return id == null ? null : String(id)
}

/**
 * itemId → `true` cuando el seller paga el envío de ese ítem
 * (`datosCanal.shipping.freeShipping`, señal canónica — sin números mágicos,
 * sirve multipaís).
 *
 * Sólo se consultan las entradas en `true`: un mapa que traiga únicamente los
 * que califican (el que arma el backend con su query filtrada) produce
 * exactamente el mismo reparto que uno completo. Tiene test.
 */
export function construirEnvioGratisPorItem(publicaciones = []) {
  const map = new Map()
  for (const p of publicaciones) {
    const itemId = itemIdDePublicacion(p)
    if (itemId == null) continue
    map.set(itemId, p?.datosCanal?.shipping?.freeShipping === true)
  }
  return map
}

/**
 * Envío del seller de la ORDEN, con su origen: `{ monto, origen }`. Lector del
 * shape crudo de Mercado Libre (`_enriched`, `payments[]`): el envío de Tienda
 * Nube sale de su propio adapter (`tienda-nube/ordenCascada.js`), no de acá.
 *
 * Cascada: `shippingCosts.senderFee` → `shipment.cost` (la proyección que
 * escribe la sincro de envíos). Sin ninguno de los dos:
 *   - la orden declara que no tiene envío (`envioId` presente y en null:
 *     retiro, digital) → 0 conocido, origen `api`;
 *   - si no (tiene envío, o no se sabe porque la clave no vino) → 0 con origen
 *     `no-informada`: falla cerrado, nunca un 0 presentado como dato.
 * Nunca `payments[].shipping_cost`: es lo que pagó el COMPRADOR (Sincro A,
 * A.6b-4 parte 3). La cascada no lee la colección `envios` directo: «va a
 * envíos» es la proyección `shipment`, y una segunda fuente ampliaría la
 * excepción transitoria de la Decisión 7 del ADR de Sincro A.
 * El origen viaja a la línea persistida (`origenes.envio`) y a la del front, y
 * las pantallas derivan el «no informado» de ahí (`derivarCostosNoInformados`).
 */
export function envioSellerDeOrden(orden) {
  const enr = orden?._enriched
  const senderFee = enr?.shippingCosts?.senderFee
  if (senderFee != null) return { monto: num(senderFee), origen: ORIGENES_COSTO.API }
  const costoShipment = enr?.shipment?.cost
  if (costoShipment != null) return { monto: num(costoShipment), origen: ORIGENES_COSTO.API }
  const sinEnvio = orden != null && Object.prototype.hasOwnProperty.call(orden, 'envioId') && orden.envioId == null
  return { monto: 0, origen: sinEnvio ? ORIGENES_COSTO.API : ORIGENES_COSTO.NO_INFORMADA }
}

/**
 * Reparte el costo de envío del seller entre las líneas de una orden.
 *
 * @param {Object}  args
 * @param {Array}   args.items              — `order_items` crudos de ML
 * @param {number}  args.envioTotal         — costo de envío del seller de la ORDEN
 * @param {number}  args.subtotalOrden      — denominador del fallback
 * @param {Map|null} args.envioGratisPorItem — itemId → bool (opcional)
 * @returns {{ porLinea: number[], criterio: 'solo-califican'|'todas-por-subtotal' }}
 *          `porLinea` es paralelo a `items`.
 */
export function repartirEnvioSeller({
  items = [],
  envioTotal = 0,
  subtotalOrden = 0,
  envioGratisPorItem = null,
} = {}) {
  const lista = Array.isArray(items) ? items : []
  const tieneMapa = envioGratisPorItem instanceof Map
  const califica = (oi) => {
    if (!tieneMapa) return false
    const iid = itemIdDeOrderItem(oi)
    return iid != null && envioGratisPorItem.get(iid) === true
  }

  let subtotalConEnvio = 0
  if (tieneMapa) {
    for (const oi of lista) {
      if (califica(oi)) subtotalConEnvio += num(oi?.unit_price) * num(oi?.quantity)
    }
  }
  const soloCalifican = tieneMapa && subtotalConEnvio > 0

  const total = num(envioTotal)
  const denomFallback = num(subtotalOrden)
  const porLinea = lista.map((oi) => {
    const subtotal = num(oi?.unit_price) * num(oi?.quantity)
    let prorrateoEnvio
    if (soloCalifican) {
      prorrateoEnvio = califica(oi) ? subtotal / subtotalConEnvio : 0
    } else {
      prorrateoEnvio = denomFallback > 0 ? subtotal / denomFallback : 0
    }
    return total * prorrateoEnvio
  })

  return { porLinea, criterio: soloCalifican ? 'solo-califican' : 'todas-por-subtotal' }
}
